import { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector, batch } from 'react-redux';

import { EventsState } from '../../../../../store/types';
import { AnalyticsSession } from '../../../../../types/working-model';
import ListHeader from '../../components/Scaffolding/ListHeader';
import Scorecard from '../../components/Scorecard/Scorecard';
import SessionListElement from '../../components/SessionListElement';
import AudienceMap from '../../components/Map/AudienceMap';
import ComponentWithHeader from '../../components/Scaffolding/ComponentWithHeader';
import AnimatedPieSVG from '../../components/PieChart/PieChartAnimated';
import BarChartAudienceLocation from '../../components/BarChart/BarChartAudienceLocation';
import BarChartAudienceRetention from '../../components/BarChart/BarChartAudienceRetention';
import { useParams } from "react-router-dom";
import { getEventDeviceTypes, getEventDuration, getEventRegions, getEventRetentionAverage, getEventViews, getEventRegistrations, getEventWatchtime, getEventRetentionBuckets, getEventBrowsers, getEventEmails, getEventSessions, getEventLanguages, getEventCountryMap } from '../../../../../store/actions/admin/analytics';
import usePrevious from '../../../../../utils/use-previous';
import { tooltipText } from './ClientLevelEventAnalytics';

export interface ParamsProps {
	eventUuid: string;
}

const sortOptions = [
	{
		label: 'Newest',
		value: 'newest',
	},
	{
		label: 'Oldest',
		value: 'oldest',
	},
	{
		label: 'A-Z',
		value: 'a-z',
	},
	{
		label: 'Z-A',
		value: 'z-a',
	},
];

function EventLevelAnalytics(): JSX.Element {
	const {
		AuthReducer: { user, token },
		AnalyticsReducer: { eventSessions, eventDevices, eventRegions, eventLanguages, eventViews, eventDuration, eventRetentionAverage, eventRegistrations, eventWatchtime, eventRetentionBuckets, eventBrowsers, eventEmails, eventMapData }
	} = useSelector((state: EventsState) => state);

	const { eventUuid }: ParamsProps = useParams();

	const dispatch = useDispatch();

	const lastChannel = usePrevious(user?.active_channel);

	useEffect(() => {
		if (lastChannel != user?.active_channel) {
			batch(() => {
				dispatch(getEventSessions(eventUuid, token!));
				dispatch(getEventDeviceTypes(eventUuid, token!));
				dispatch(getEventRegions(eventUuid, token!));
				dispatch(getEventLanguages(eventUuid, token!));
				dispatch(getEventViews(eventUuid, token!));
				dispatch(getEventDuration(eventUuid, token!));
				dispatch(getEventRetentionAverage(eventUuid, token!));
				dispatch(getEventRetentionBuckets(eventUuid, token!));
				dispatch(getEventRegistrations(eventUuid, token!));
				dispatch(getEventEmails(eventUuid, token!));
				dispatch(getEventWatchtime(eventUuid, token!));
				dispatch(getEventBrowsers(eventUuid, token!));
				dispatch(getEventCountryMap(eventUuid, token!));
			});
		}
	}, [user, lastChannel, token, eventUuid, dispatch]);

	const [filteredSessions, setFilteredSessions] = useState<AnalyticsSession[]>([]);
	const [sortBy, setSortBy] = useState('');

	useEffect(() => {
		setFilteredSessions(eventSessions.data);
	}, [eventSessions]);

	const handleSearch = (search: string): void => {
		const filteredSessions: AnalyticsSession[] = eventSessions.data.filter((session: AnalyticsSession) => {
			const sessionName = session.title.changed?.toLowerCase() || session.title.base.toLowerCase();
			return sessionName.includes(search.toLowerCase().trim());
		});
		setFilteredSessions(filteredSessions);
	};

	const handleClear = (): void => {
		setFilteredSessions(eventSessions.data);
	};

	const sortedSessions = useMemo((): AnalyticsSession[] => {
		const sortedList = [...filteredSessions];
		switch (sortBy) {
			case 'oldest': {
				sortedList.sort((a, b) => {
					const aStart = a.timestamp || '';
					const bStart = b.timestamp || '';
					if (aStart < bStart) return -1;
					if (aStart > bStart) return 1;
					return 0;
				});
				break;
			}
			case 'a-z': {
				sortedList.sort((a, b) => {
					const nameA = a.title.base.toLowerCase();
					const nameB = b.title.base.toLowerCase();
					if (nameA < nameB) return -1;
					if (nameA > nameB) return 1;
					return 0;
				});
				break;
			}
			case 'z-a': {
				sortedList.sort((a, b) => {
					const nameA = a.title.base.toLowerCase();
					const nameB = b.title.base.toLowerCase();
					if (nameA > nameB) return -1;
					if (nameA < nameB) return 1;
					return 0;
				});
				break;
			}
			case 'newest':
			default: {
				sortedList.sort((a, b) => {
					const aStart = a.timestamp || '';
					const bStart = b.timestamp || '';
					if (aStart > bStart) return -1;
					if (aStart < bStart) return 1;
					return 0;
				});
			}
		}
		return sortedList;
	}, [sortBy, filteredSessions]);
	return (

		<div className="data-analytics">
			<div className="event-metrics-grid">
				<Scorecard data={eventViews.data} index={0} showTL={true} showMM={false} title="Total Views" loading={eventViews.loading} error={eventViews.error} tooltipText={tooltipText.totalViews} interval="session" />
				<Scorecard data={eventDuration.data} index={1} showTL={true} showMM={false} title="Avg. Duration, min" loading={eventDuration.loading} error={eventDuration.error} tooltipText={tooltipText.avgDuration} interval="session" />
				<Scorecard data={eventRetentionAverage.data} index={2} showTL={true} showMM={false} title="Retention, %" loading={eventRetentionAverage.loading} error={eventRetentionAverage.error} tooltipText={tooltipText.retentionPercent} interval="session" />
				<Scorecard data={eventRegistrations.data} index={3} showTL={true} showMM={false} title="Total Registrations" loading={eventRegistrations.loading} error={eventRegistrations.error} tooltipText={tooltipText.totalRegistrations} interval="session" />
				<Scorecard data={eventWatchtime.data} index={4} showTL={true} showMM={false} title="Total Watchtime, min" loading={eventWatchtime.loading} error={eventWatchtime.error} tooltipText={tooltipText.totalWatchtime} interval="session" />
				{/* <Scorecard data={scorecards.pageviews} index={5} showTL={true} showMM={false} title="Page Views" /> */}
				{/* <Scorecard data={scorecards.interactionScore} index={6} showTL={true} showMM={false} title="Interations Score" /> */}
				<Scorecard data={eventEmails.data} index={7} showTL={false} showMM={false} title="Emails" loading={eventEmails.loading} error={eventEmails.error} tooltipText={tooltipText.emails} />
				<Scorecard data={{ value: eventSessions.data.length }} index={8} showTL={false} showMM={false} title="Sessions" loading={eventSessions.loading} error={eventSessions.error} tooltipText={tooltipText.sessions} />

				<ComponentWithHeader X={"X2"} Y={"Y2"} id={["donut-chart", 0]} header={"Device Type"} tooltipText={tooltipText.deviceType}>
					<AnimatedPieSVG data={{ values: eventDevices.data }} index={0} title={"Device Type"} loading={eventDevices.loading} error={eventDevices.error} />
				</ComponentWithHeader>

				<ComponentWithHeader X={"X2"} Y={"Y2"} id={["donut-chart", 1]} header={"Region"} tooltipText={tooltipText.region}>
					<AnimatedPieSVG data={{ values: eventRegions.data }} index={1} title={"Region"} loading={eventRegions.loading} error={eventRegions.error} />
				</ComponentWithHeader>

				<ComponentWithHeader X={"X6"} Y={"Y4"} id={["event-retention", 0]} header={"Audience Retention"} tooltipText={tooltipText.audienceRetention}>
					<BarChartAudienceRetention width={720} height={320} dataIn={eventRetentionBuckets.data} loading={eventRetentionBuckets.loading} error={eventRetentionBuckets.error} />
				</ComponentWithHeader>

				{/* <ComponentWithHeader X={"X6"} Y={"Y4"} id={["event-retention", 1]} header={"Audience Retention"}>
                    <BarChartAudienceRetention width={720} height={320} dataIn={retentionData}/>
                </ComponentWithHeader>  */}

				<ComponentWithHeader X={"X2"} Y={"Y2"} id={["donut-chart", 2]} header={"Browser"} tooltipText={tooltipText.browser}>
					<AnimatedPieSVG data={{ values: eventBrowsers.data }} index={2} title={"Browser"} loading={eventBrowsers.loading} error={eventBrowsers.error} />
				</ComponentWithHeader>

				<ComponentWithHeader X={"X2"} Y={"Y2"} id={["donut-chart", 3]} header={"Languages"} tooltipText={tooltipText.languages}>
					<AnimatedPieSVG data={{ values: eventLanguages.data }} index={3} title={"Languages"} loading={eventLanguages.loading} error={eventLanguages.error} />
				</ComponentWithHeader>

				<ComponentWithHeader X={"X6"} Y={"Y4"} id={["world-map", 0]} header={"Audience Location"} tooltipText={tooltipText.audienceLocation}>
					<AudienceMap width={408} height={280} countryMapData={eventMapData.data} loading={eventMapData.loading} error={eventMapData.error} />
					<BarChartAudienceLocation data={eventMapData.data} loading={eventMapData.loading} error={eventMapData.error} />
				</ComponentWithHeader>

			</div>

			<ListHeader
				activeTab={"Sessions"}
				activeTabCount={eventSessions.data.length}
				handleSearch={handleSearch}
				handleClear={handleClear}
				sortOptions={sortOptions}
				sortBy={sortBy}
				setSortBy={setSortBy}
			/>

			<div className="events-list">
				{sortedSessions.map((e) => <SessionListElement key={e.session} sessionData={e} />)}
			</div>
		</div>
	);
}

export default EventLevelAnalytics;
